import React, { useMemo } from 'react';
import { isValid, parse } from 'date-fns';
import { ButtonV2, CheckboxV2, DatePickerInput, FormControl, InputV2, LinkV2, SpacingStack, TextV2, Flex, useDisclosure, DialogV2, Box } from '@withjoy/joykit';
import WeddingHeroText from '@apps/createWedding/routes/CreateWeddingForm/components/WeddingHeroText';
import { FormWrapperStack, styles } from '@apps/createWedding/routes/CreateWeddingForm/CreateWeddingForm.styles';
import { useTranslation } from '@shared/core';
import { useCreateWeddingFormController, CreateWeddingFormProps } from '@apps/createWedding/routes/CreateWeddingForm/CreateWeddingForm.controller';
import { wrapWithCreateWeddingFormHealthProvider } from './CreateWeddingForm.Health';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { AdvancedLocationInputV2FormFields, LocationInputV2Expandable, withFormikLocationInputV2 } from '@shared/components/LocationInputV2';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { FormikProvider } from 'formik';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { CreateWeddingFormSkeleton } from './CreateWeddingForm.Skeleton';
import { useIsCreateWeddingReady } from '@apps/createWedding/hooks/useIsCreateWeddingReady';
import { useLocaleContext } from '@localization/context/LocaleContext';
import { useSurveySkip } from '../Survey/useSurveySkip';

const LocationInputV2ExpandableWithFormik = withFormikLocationInputV2(LocationInputV2Expandable);

const InputLabel: React.FC = ({ children }) => <TextV2 typographyVariant="label3">{children}</TextV2>;

export const CreateWeddingForm = wrapWithCreateWeddingFormHealthProvider<CreateWeddingFormProps>(props => {
  const { t, tq } = useTranslation('createWedding');
  const {
    formik,
    user,
    getFieldErrorProps,
    signOut,
    blockWeddingDate,
    setBlockWeddingDate,
    blockEventVenue,
    setBlockEventVenue,
    telemetry,
    isCustomEventDialogOpen,
    onCloseCustomEventDialog,
  } = useCreateWeddingFormController(props);

  const weddingFormTranslations = t('weddingForm');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isReadyCreateWedding = useIsCreateWeddingReady();

  const isMobile = useIsMobileScreen();
  const { payload } = useRemoteFeatureFlag('venueAddressLocationEnabled');
  const placePredictionTypes = useMemo(() => (Object(payload)?.types as string[]) || [], [payload]);
  const { dateFormat } = useLocaleContext();

  const handleOnChangeLocation = useEventCallback((values: AdvancedLocationInputV2FormFields) => {
    if (values.latitude && values.longitude && values.placeId) {
      formik.setFieldValue('eventVenue', values);
      telemetry.inputHandler('eventVenue', 'onChange');
      // Save event location same as venue - https://joylife.slack.com/archives/CDWBWGY69/p1758213644418509?thread_ts=1758213586.836009&cid=CDWBWGY69
      formik.setFieldValue('location', { place: values.formattedAddress, placeId: values.placeId, lat: values.latitude, lng: values.longitude });
    }
  });

  const changeAccount = weddingFormTranslations.changeAccount({
    email: user.email,
    SignOut: () => (
      <LinkV2
        onClick={signOut}
        {...styles.signOutLinkStyles}
        typographyVariant="button1"
        textDecoration="none"
      >
        {weddingFormTranslations.signOut()}
      </LinkV2>
    ),
  });

  const handleDateInputChange = useEventCallback((dateString: string) => {
    const isSameLength = dateString.replace(/(\s)*/g, '').length === dateFormat.replace(/(\s)*/g, '').length;
    const inputDate = parse(dateString, dateFormat, new Date());
    if (!isSameLength || !isValid(inputDate)) {
      formik.setFieldValue('isDateValid', false);
    }
  });

  return (
    <FormWrapperStack
      width="100%"
      marginX="auto"
      spacing={8}
    >
      <SkeletonGroup
        isReady={isReadyCreateWedding}
        width="100%"
        gap={8}
        display="flex"
        flexDirection="column"
        placeholder={<CreateWeddingFormSkeleton />}
      >
        <ResponsiveDialog
          isOpen={isCustomEventDialogOpen}
          onClose={onCloseCustomEventDialog}
          hideDivider={true}
          hasCloseButton={false}
          dialogOverrides={{
            Body: {
              props: {
                paddingTop: '0!important',
              },
            },
          }}
        >
          <Box position="relative">
            <DialogV2.CloseButton
              marginRight={-6}
              right={0}
            />
            <Flex
              flexDirection="column"
              alignItems="center"
              rowGap={6}
              paddingTop={10}
              marginTop={{
                sm: -6,
              }}
            >
              <TextV2>{weddingFormTranslations.customEvent.description()}</TextV2>
              <ButtonV2 onClick={onCloseCustomEventDialog}>{weddingFormTranslations.customEvent.buttonTitle()}</ButtonV2>
            </Flex>
          </Box>
        </ResponsiveDialog>
        <WeddingHeroText />
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <SpacingStack spacing={5}>
              <FormControl
                onFocus={() => telemetry.inputHandler('ownerFirstName', 'onFocus')}
                {...getFieldErrorProps('ownerFirstName')}
                label={<InputLabel>{weddingFormTranslations.ownerFirstName()}</InputLabel>}
              >
                <InputV2
                  placeholder={weddingFormTranslations.ownerFirstNamePlaceholder()}
                  {...formik.getFieldProps('ownerFirstName')}
                  autoComplete="given-name"
                />
              </FormControl>
              {/* Hidden field to collect user last name by autofill - type=hidden does not autofill value */}
              <InputV2
                {...formik.getFieldProps('ownerLastName')}
                autoComplete="family-name"
                overrides={{
                  Root: { props: { borderWidth: 0, marginTop: '0px !important', _after: { display: 'none' } } },
                  Input: { props: { position: 'absolute', left: '-9999px', height: '1px', width: '1px', opacity: 0 } },
                }}
              />
              <FormControl
                onFocus={() => telemetry.inputHandler('partnerFirstName', 'onFocus')}
                {...getFieldErrorProps('partnerFirstName')}
                label={<InputLabel>{weddingFormTranslations.partnerFirstName()}</InputLabel>}
              >
                <InputV2
                  placeholder={weddingFormTranslations.partnerFirstNamePlaceholder()}
                  {...formik.getFieldProps('partnerFirstName')}
                />
              </FormControl>
              <SpacingStack spacing={5}>
                <LocationInputV2ExpandableWithFormik
                  name="eventVenue"
                  label={tq`Event Venue`}
                  placeholders={[tq`San Francisco City Hall`]}
                  placePredictionTypes={placePredictionTypes}
                  initialValues={{ placeId: formik.values.eventVenue?.placeId || '', locationValue: formik.values.eventVenue?.locationValue || '' }}
                  intent={'border'}
                  allowCustomPlace={false}
                  shouldCloseOnSelect
                  hasPopOver={!isMobile}
                  isHideListItemIcon={true}
                  onChangeLocation={handleOnChangeLocation}
                  shape="square"
                  isDisabled={blockEventVenue}
                  errorProps={{ textAlign: 'left' }}
                />
                <CheckboxV2
                  checked={blockEventVenue}
                  onChange={e => {
                    setBlockEventVenue(e.target.checked);
                    telemetry.inputHandler('eventVenue', 'onChange');
                  }}
                  fontWeight={500}
                >
                  {tq`We haven't picked a venue (yet)`}
                </CheckboxV2>
              </SpacingStack>
              <SpacingStack spacing={5}>
                <FormControl
                  onFocus={() => telemetry.inputHandler('eventDate', 'onFocus')}
                  {...getFieldErrorProps('weddingDate')}
                  label={<InputLabel>{weddingFormTranslations.weddingDate()}</InputLabel>}
                >
                  <DatePickerInput
                    isDisabled={blockWeddingDate}
                    endElement={null}
                    mask={null}
                    minDate={new Date()}
                    dateFormat={dateFormat}
                    placeholder={dateFormat}
                    {...formik.getFieldProps('weddingDate')}
                    onFocus={e => e.stopPropagation()}
                    onOpen={onOpen}
                    onClose={onClose}
                    isOpen={isOpen}
                    onChange={date => {
                      formik.setFieldValue('isDateValid', true);
                      formik.setFieldValue('weddingDate', date);
                      telemetry.inputHandler('eventDate', 'onChange');
                    }}
                    onInputChange={handleDateInputChange}
                    enableCloseOnSelect={true}
                  />
                </FormControl>
                <CheckboxV2
                  checked={blockWeddingDate}
                  onChange={e => {
                    setBlockWeddingDate(e.target.checked);
                    telemetry.inputHandler('eventDate', 'onChange');
                  }}
                  fontWeight={500}
                >
                  {weddingFormTranslations.skipDateDefinition()}
                </CheckboxV2>
              </SpacingStack>
            </SpacingStack>
          </form>
        </FormikProvider>
        <SpacingStack spacing={6}>
          <ButtonV2
            shape="rounded"
            loading={props.isCreateCtaLoading || formik.isSubmitting}
            type="submit"
            onClick={formik.submitForm}
            intent="primary"
          >
            {weddingFormTranslations.createWeddingCta()}
          </ButtonV2>
          <TextV2
            textAlign="center"
            typographyVariant="body1"
            color="mono12"
          >
            {changeAccount}
          </TextV2>
        </SpacingStack>
      </SkeletonGroup>
    </FormWrapperStack>
  );
});

export const CreateWeddingFormWithSkipSurvey = () => {
  const [isCreateWeddingSkipSurveyExperimentEnabled] = useIsFeatureEnabled('createWeddingSkipSurveyExperiment');
  const { onSurveySkip } = useSurveySkip();
  if (isCreateWeddingSkipSurveyExperimentEnabled) {
    return (
      <CreateWeddingForm
        showQuestionnaire={false}
        onEventCreateSuccess={onSurveySkip}
      />
    );
  }
  return <CreateWeddingForm showQuestionnaire={true} />;
};

export default CreateWeddingFormWithSkipSurvey;
